export { getLocaleShopUrl } from "./locale-shop";

export type CountryCode = "AT" | "CH" | "IT" | "PL" | "CZ" | "LU" | "DE";

export class CountryMapper {
  static countryMapping: Record<
    CountryCode,
    {
      availableLanguages: string[];
      defaultLanguage: string;
    }
  > = {
    AT: {
      availableLanguages: ["de"],
      defaultLanguage: "de",
    },
    CH: {
      availableLanguages: ["de", "fr", "it"],
      defaultLanguage: "de",
    },
    IT: {
      availableLanguages: ["de", "it"],
      defaultLanguage: "it",
    },
    PL: {
      availableLanguages: ["pl"],
      defaultLanguage: "pl",
    },
    CZ: {
      availableLanguages: ["cs"],
      defaultLanguage: "cs",
    },
    LU: {
      availableLanguages: ["lb", "fr", "de"],
      defaultLanguage: "lb",
    },
    DE: {
      availableLanguages: ["de"],
      defaultLanguage: "de",
    },
  };

  public static normaliseLocale(locale: string | null) {
    if (locale !== undefined && locale !== null) {
      const caseInsensitiveMatches = locale.match(
        /^([A-Za-z][A-Za-z])-([A-Za-z][A-Za-z])$/,
      );
      if (caseInsensitiveMatches != null) {
        return `${caseInsensitiveMatches[1].toLowerCase()}-${caseInsensitiveMatches[2].toUpperCase()}`;
      }
    }
    return null;
  }

  public static isValidLocale(locale: string | null): boolean {
    return (
      locale !== undefined &&
      locale !== null &&
      locale.match(/^[a-z][a-z]-[A-Z][A-Z]$/) !== null
    );
  }

  public static validLocaleOrDefault(
    locale: string | null,
    defaultLocale: string | null,
  ): string | null {
    return CountryMapper.isValidLocale(locale) ? locale : defaultLocale;
  }

  public static parseLanguageCode(locale: string | null): string | null {
    if (locale === undefined || locale === null) {
      return null;
    }
    const match = locale.match(/^([a-z][a-z])-([A-Z][A-Z])$/);
    if (!match) return null;
    return match[1];
  }

  public static parseCountryCodeWhenSupported(
    locale: string | null,
  ): CountryCode | null {
    if (locale === undefined || locale === null) {
      return null;
    }
    const match = locale.match(/^([a-z][a-z])-([A-Z][A-Z])$/);
    if (!match) return null;
    const countryCode = match[2] as CountryCode;
    if (!Object.keys(CountryMapper.countryMapping).includes(countryCode)) {
      return null;
    }
    return countryCode;
  }

  public static isSupportedCountryCode(countryCode: string) {
    return (
      countryCode !== undefined &&
      countryCode !== null &&
      Object.keys(CountryMapper.countryMapping).includes(countryCode)
    );
  }

  public static isSupportedLocale(locale: string) {
    return locale !== undefined && this.getSupportedLocales().includes(locale);
  }

  public static getSupportedLocales(): string[] {
    return Object.keys(CountryMapper.countryMapping).reduce(
      (languages, countryCode) => {
        return languages.concat(
          CountryMapper.countryMapping[
            countryCode as CountryCode
          ].availableLanguages.map(
            (languageCode) => `${languageCode}-${countryCode}`,
          ),
        );
      },
      [] as string[],
    );
  }

  public static getLocaleTitle(locale: string) {
    if (!locale) {
      return undefined;
    }
    const languageCode = CountryMapper.parseLanguageCode(locale);
    if (!languageCode) {
      return undefined;
    }
    switch (languageCode) {
      case "de":
        return "Deutsch";
      case "fr":
        return "Français";
      case "it":
        return "Italiano";
      case "pl":
        return "Polski";
      case "cs":
        return "Čeština";
      case "lb":
        return "Lëtzebuergesch";
      default:
        return languageCode;
    }
  }

  public static getDefaultLocale(
    countryCode: keyof typeof CountryMapper.countryMapping,
  ): string {
    return `${CountryMapper.countryMapping[countryCode].defaultLanguage}-${countryCode}`;
  }

  public static getAvailableLocales(
    countryCode: keyof typeof CountryMapper.countryMapping,
  ) {
    return CountryMapper.countryMapping[countryCode].availableLanguages.map(
      (lang) => `${lang}-${countryCode}`,
    );
  }

  public static hasMultipleLocales(
    countryCode: keyof typeof CountryMapper.countryMapping,
  ) {
    return (
      CountryMapper.countryMapping[countryCode].availableLanguages.length > 1
    );
  }
}
